import React, { useEffect, useRef, useState, useCallback } from 'react';

const VNCConsole = ({ nodeId, node, vmid, vmType, vmName, onClose }) => {
    const canvasRef = useRef(null);
    const rfbRef = useRef(null);
    const [status, setStatus] = useState('Inicializando...');
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const cleanup = useCallback(() => {
        if (rfbRef.current) {
            try {
                rfbRef.current.disconnect();
            } catch (e) {
                console.error('Error disconnecting RFB:', e);
            }
            rfbRef.current = null;
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const initVNC = async () => {
            try {
                if (!mounted) return;
                
                setStatus('Obtendo credenciais VNC...');
                setError(null);
                
                // Get VNC connection info from backend
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/vm/${nodeId}/${vmType}/${vmid}/console`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Falha ao obter credenciais VNC');
                }

                const vncData = await response.json();
                
                if (!mounted) return;
                
                setStatus('Carregando cliente VNC...');

                // Dynamic import to handle ES modules properly
                const RFBModule = await import('@novnc/novnc/core/rfb.js');
                const RFB = RFBModule.default;
                
                if (!mounted) return;
                
                setStatus('Conectando ao console...');

                // Create WebSocket URL for our proxy
                const wsUrl = vncData.websocketUrl.replace('http://', 'ws://').replace('https://', 'wss://');
                
                console.log('Connecting to VNC WebSocket:', wsUrl);
                
                // Wait a bit to ensure canvas is ready
                await new Promise(resolve => setTimeout(resolve, 100));
                
                if (!canvasRef.current || !mounted) return;
                
                // Initialize noVNC RFB with minimal options first
                const rfb = new RFB(canvasRef.current, wsUrl);

                rfbRef.current = rfb;

                // Configure RFB settings after creation - be more conservative
                rfb.scaleViewport = false; // Start without scaling to avoid issues
                rfb.resizeSession = false; // Don't resize initially
                rfb.showDotCursor = true;
                rfb.background = '#000000';
                
                // Use more conservative quality settings
                rfb.qualityLevel = 9; // Higher quality, less compression
                rfb.compressionLevel = 0; // No compression initially
                
                // Set focus policy
                rfb.focusOnClick = true;
                
                console.log('noVNC RFB initialized with conservative settings');

                // Event handlers
                rfb.addEventListener('connect', (e) => {
                    console.log('VNC connected successfully:', e);
                    if (mounted) {
                        setStatus('Conectado');
                        setError(null);
                        setIsConnected(true);
                    }
                });

                rfb.addEventListener('disconnect', (e) => {
                    console.log('VNC disconnected:', e.detail);
                    console.log('Disconnect clean:', e.detail.clean);
                    console.log('Disconnect reason:', e.detail.reason);
                    if (mounted) {
                        setIsConnected(false);
                        if (e.detail.clean) {
                            setStatus('Desconectado');
                        } else {
                            setStatus('Conexão perdida');
                            setError(`Conexão perdida: ${e.detail.reason || 'Motivo desconhecido'}`);
                        }
                    }
                });

                rfb.addEventListener('credentialsrequired', (e) => {
                    console.log('VNC credentials required:', e.detail);
                    if (mounted) {
                        // Use the VNC ticket from Proxmox as password
                        try {
                            console.log('Sending VNC ticket as password for authentication...');
                            rfb.sendCredentials({ password: vncData.ticket });
                        } catch (credError) {
                            console.error('Error sending VNC ticket credentials:', credError);
                            // Fallback to empty password
                            try {
                                console.log('Fallback: Sending empty VNC password...');
                                rfb.sendCredentials({ password: '' });
                            } catch (credError2) {
                                console.error('Error sending empty credentials:', credError2);
                                setError('Falha na autenticação VNC');
                            }
                        }
                    }
                });

                rfb.addEventListener('securityfailure', (e) => {
                    console.error('VNC security failure:', e.detail);
                    if (mounted) {
                        setError(`Falha de segurança VNC: ${e.detail.reason}`);
                    }
                });

                rfb.addEventListener('desktopname', (e) => {
                    console.log('VNC desktop name:', e.detail.name);
                });

                rfb.addEventListener('bell', () => {
                    console.log('VNC bell');
                });

                // Add more debugging events
                rfb.addEventListener('clipboard', (e) => {
                    console.log('VNC clipboard event:', e.detail);
                });

                rfb.addEventListener('fbresize', (e) => {
                    console.log('VNC framebuffer resize:', e.detail);
                });

                // Add error handling for WebSocket
                const wsUrl_debug = wsUrl;
                
                // Monitor WebSocket events
                setTimeout(() => {
                    const OriginalWebSocket = window.WebSocket;
                    window.WebSocket = function(url, protocols) {
                        const ws = new OriginalWebSocket(url, protocols);
                        if (url === wsUrl_debug) {
                            console.log('VNC WebSocket created for:', url);
                            
                            ws.addEventListener('open', () => {
                                console.log('VNC WebSocket opened');
                            });
                            
                            ws.addEventListener('close', (e) => {
                                console.log('VNC WebSocket closed:', e.code, e.reason);
                            });
                            
                            ws.addEventListener('error', (e) => {
                                console.log('VNC WebSocket error:', e);
                            });
                        }
                        return ws;
                    };
                }, 100);

            } catch (err) {
                console.error('VNC initialization error:', err);
                if (mounted) {
                    setError(err.message);
                    setStatus('Erro');
                    setIsConnected(false);
                }
            }
        };

        initVNC();

        // Cleanup function
        return () => {
            mounted = false;
            cleanup();
        };
    }, [nodeId, vmid, cleanup]);

    const handleFullscreen = () => {
        const element = canvasRef.current?.parentElement;
        if (element) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            }
        }
    };

    const handleCtrlAltDel = () => {
        if (rfbRef.current && isConnected) {
            try {
                rfbRef.current.sendCtrlAltDel();
            } catch (e) {
                console.error('Error sending Ctrl+Alt+Del:', e);
            }
        }
    };

    const handleReconnect = () => {
        setError(null);
        setStatus('Reconectando...');
        setIsConnected(false);
        cleanup();
        // Force re-render to trigger useEffect
        // setTimeout(() => {
        //     window.location.reload();
        // }, 1000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-full m-4 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
                    <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-semibold">Console - {vmName}</h3>
                        <span className={`px-2 py-1 rounded text-sm ${
                            isConnected ? 'bg-green-100 text-green-800' :
                            error ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                        }`}>
                            {error || status}
                        </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleCtrlAltDel}
                            disabled={!isConnected}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Ctrl+Alt+Del
                        </button>
                        <button
                            onClick={handleFullscreen}
                            disabled={!isConnected}
                            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Tela Cheia
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                            Fechar
                        </button>
                    </div>
                </div>

                {/* VNC Display Container */}
                <div className="flex-1 bg-black relative overflow-hidden">
                    {error ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center text-white">
                                <div className="text-red-400 text-xl mb-2">❌</div>
                                <div className="text-lg mb-2">Erro de Conexão</div>
                                <div className="text-sm opacity-75 mb-4">{error}</div>
                                <div className="space-x-2">
                                    <button
                                        onClick={handleReconnect}
                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Tentar Novamente
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : !isConnected ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center text-white">
                                <div className="animate-spin text-4xl mb-4">⏳</div>
                                <div className="text-lg">{status}</div>
                                <div className="text-sm opacity-75 mt-2">
                                    Estabelecendo conexão com o console da VM...
                                </div>
                            </div>
                        </div>
                    ) : null}
                    
                    {/* Canvas for VNC display */}
                    <div 
                        ref={canvasRef}
                        className="w-full h-full"
                        style={{ 
                            minHeight: '400px'
                        }}
                    />
                </div>

                {/* Footer */}
                <div className="p-2 bg-gray-50 border-t text-center text-sm text-gray-600 rounded-b-lg">
                    <div className="flex justify-between items-center">
                        <span>
                            Console VNC via noVNC
                        </span>
                        <span className={`inline-flex items-center ${
                            isConnected ? 'text-green-600' : 'text-red-600'
                        }`}>
                            <div className={`w-2 h-2 rounded-full mr-2 ${
                                isConnected ? 'bg-green-400' : 'bg-red-400'
                            }`}></div>
                            {isConnected ? 'Conectado' : 'Desconectado'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VNCConsole;